import { gql } from "@apollo/client";
import _ from "lodash";
import {
  AtSignIcon,
  BuildingIcon,
  ListTreeIcon,
  TagsIcon,
  TextAlignStartIcon,
} from "lucide-react";

import { ChartedAccountFragment } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Values } from "@/constants/values";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";

const object: ObjectType<ChartedAccountFragment> = {
  humanName: "charted account",
  typename: "ChartedAccountObject",
  report: {
    source: "charted_accounts",
    description: "The accounts the ledger is kept in",
    asks: "Which accounts carry the biggest balances?",
  },
  ReactIcon: ListTreeIcon,
  isEntity: false,
  titleFieldKey: "name",
  getTitle: (data) => {
    if (data?.path) {
      return data?.path + ":" + data?.name;
    }

    return undefined;
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  getReferenceValueOptions: (entityIdentifiers) => ({
    query: gql`
      query ChartedAccountList($entityIdentifiers: [String!]) {
        chartedAccounts(entityIdentifiers: $entityIdentifiers) {
          data {
            id
            identifier
            path
            fullPath
            name
            description
            entity {
              id
              identifier
              name
            }
          }
        }
      }
    `,
    queryOptions: {
      variables: {
        entityIdentifiers,
      },
    },
    filterOptions: (options: ChartedAccountFragment[], filter: string) => {
      return options.filter((option) =>
        option.name.toLowerCase().includes(filter.toLowerCase()),
      );
    },
    onRenderOption(
      chartedAccount: ChartedAccountFragment & {
        fullPath: string[];
      },
    ) {
      return chartedAccount.path.slice(0, -1) + ":" + chartedAccount.name;
    },
  }),
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Charted Account",
      type: "reference",
      ReactIcon: iconsByTypename.ChartedAccountObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "ChartedAccountObject",
          name: data?.path ? data?.path + ":" + data?.name : data?.name,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("ChartedAccountObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      getValueOptions: () => ({
        placeholder: "Prep work, etc.",
      }),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.description,
      getValue: (data) => {
        return data?.description;
      },
      getValueOptions: () => ({
        placeholder: "Used for tracking project costs, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "parent",
      // inputKey: "parent",
      label: "Parent",
      type: "reference",
      ReactIcon: ListTreeIcon,
      // None until one is picked: a new account's parent is no parent yet.
      asString: (data) =>
        data?.parent ? data.parent.path + ":" + data.parent.name : "",
      getValue: (data) => {
        if (!data?.parent) return null;
        return {
          __typename: "ChartedAccountObject",
          name: data.parent.path + ":" + data.parent.name,
          id: data.parent.id,
          identifier: data.parent.identifier,
        };
      },
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("ChartedAccountObject")([
          data?.entity?.identifier,
        ]),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
    },
    // {
    //   objectKey: "path",
    //   label: "Path",
    //   type: "path",
    //   ReactIcon: AtSignIcon,
    //   asString: (data) => {
    //     const path = [data?.type];

    //     if (data?.subtype) {
    //       path.push(_.capitalize(data.subtype));
    //     }

    //     return path.join(" / ");
    //   },
    //   getValue: (data) => {
    //     const path = [data?.type];

    //     if (data?.subtype) {
    //       path.push(_.capitalize(data.subtype));
    //     }

    //     return path.join(" / ");
    //   },
    //   updateProps: {
    //     isRequired: false,
    //     isEditable: false,
    //   },
    //   summaryProps: {
    //     isVisible: true,
    //   },
    //   fieldProps: {
    //     headerFieldType: "string",
    //   },
    // },
    {
      objectKey: "categories",
      inputKey: "categories",
      label: "Categories",
      type: "references",
      ReactIcon: TagsIcon,
      asString: (data) =>
        (data?.categories && data?.categories?.length > 0
          ? data?.categories?.map((cat) => cat.name).join(", ")
          : "No categories") || "No categories",
      getValue: (data) => {
        return (data?.categories || []).map((cat) => ({
          __typename: "CategoryObject",
          name: cat.name,
          id: cat.id,
          identifier: cat.identifier,
          color: cat.color,
        }));
      },
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("CategoryObject")([
          data?.entity?.identifier,
        ]),
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
        // Its categories are edited together, as accounts under those they
        // narrow, in Manage categories.
        getEditableObjectInfo: (_, entity) => ({
          typename: "",
          id: entity?.id,
          identifier: entity?.identifier,
          commandDefinitionId: "manage-categories",
        }),
      },
    },
    {
      objectKey: "createdAt",
      label: "Created at",
      type: "text",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.createdAt,
      getValue: (data) => {
        return data?.createdAt;
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "text",
      },
    },
  ],
};

export { object as ChartedAccountObject };
